import React, { useEffect, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/20/solid";
import { generateDays } from "../../utils/Calendar";
import api from "../../api/api";

const classNames = (...classes) => {
  return classes.filter(Boolean).join(" ");
};

const convertDateToHourAndMinute = (date) => {
  return new Date(date).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const MonthView = ({ setView }) => {
  const [days, setDays] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;


      try {
        const response = await api.get(`/roster/byMonthAndYear/byEmployee/${month}/${year}`);
        const rosterData = response.data.map((roster) => ({
          id: roster.employeeId._id,
          rosterId: roster._id,
          name: `${roster.employeeId.firstName} ${roster.employeeId.lastName}`,
          shiftStart: roster.shiftStart,
          shiftEnd: roster.shiftEnd,
          datetime: roster.shiftStart,
          break: roster.breakTime,
          date: roster.date.split("T")[0],
        }));
        const newDays = generateDays(year, month - 1, rosterData);
        setDays(newDays);
        if (
          !selectedDay ||
          new Date(selectedDay.date).getMonth() !== month - 1 ||
          new Date(selectedDay.date).getFullYear() !== year
        ) {
          setSelectedDay(newDays.find((day) => day.isToday) || newDays[0]);
        }
      } catch (error) {
        console.log(error)
      }
    }

    fetchData()
  }, [currentDate]);

  const goToPreviousMonth = () =>
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
  const goToNextMonth = () =>
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
  const goToToday = () => setCurrentDate(new Date());

  return (
    <>
      <div className="lg:flex lg:h-full lg:flex-col">
        <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 lg:flex-none">
          <h1 className="text-base font-semibold text-gray-900">
            <time dateTime={currentDate.toISOString().slice(0, 7)}>
              {currentDate.toLocaleString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </time>
          </h1>
          <div className="flex items-center">
            <div className="relative flex items-center rounded-md bg-white shadow-sm md:items-stretch">
              <button
                type="button"
                onClick={goToPreviousMonth}
                className="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
              >
                <span className="sr-only">Previous month</span>
                <ChevronLeftIcon className="size-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={goToToday}
                className="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
              >
                Today
              </button>
              <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
              <button
                type="button"
                onClick={goToNextMonth}
                className="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50"
              >
                <span className="sr-only">Next month</span>
                <ChevronRightIcon className="size-5" aria-hidden="true" />
              </button>
            </div>
            <div className="hidden md:ml-4 md:flex md:items-center">
              <Menu as="div" className="relative">
                <MenuButton
                  type="button"
                  className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Month view
                  <ChevronDownIcon
                    className="-mr-1 size-5 text-gray-400"
                    aria-hidden="true"
                  />
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="py-1">
                    {/* <MenuItem>
                      <span
                        onClick={() => setView("week")}
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none hover:cursor-pointer"
                      >
                        Week view
                      </span>
                    </MenuItem> */}
                    <MenuItem>
                      <span className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none hover:cursor-pointer">
                        Month view
                      </span>
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            </div>
            <Menu as="div" className="relative ml-6 md:hidden">
              <MenuButton className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
                <span className="sr-only">Open menu</span>
                <EllipsisHorizontalIcon className="size-5" aria-hidden="true" />
              </MenuButton>
              <MenuItems
                transition
                className="absolute right-0 z-10 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                <div className="py-1">
                  <MenuItem>
                    <span 
                    onClick={goToToday}
                    className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none cursor-pointer">
                      Go to today
                    </span>
                  </MenuItem>
                </div>
                <div className="py-1">
                  {/* <MenuItem>
                    <span className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none hover:cursor-pointer">
                      Week view
                    </span>
                  </MenuItem> */}
                  <MenuItem>
                    <span className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none hover:cursor-pointer">
                      Month view
                    </span>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>
          </div>
        </header>

        <div className="shadow ring-1 ring-black/5 lg:flex lg:flex-auto lg:flex-col">
          <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs/6 font-semibold text-gray-700 lg:flex-none">
            {weekdays.map((day, index) => (
              <div key={index} className="bg-white py-2">
                {day.charAt(0)}
                <span className="sr-only sm:not-sr-only">{day.slice(1)}</span>
              </div>
            ))}
          </div>
          <div className="flex bg-gray-200 text-xs/6 text-gray-700 lg:flex-auto">
            <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
              {days.map((day) => (
                <div
                  key={day.date}
                  onClick={() => setSelectedDay(day)}
                  className={classNames(
                    day.isCurrentMonth
                      ? "bg-white"
                      : "bg-gray-50 text-gray-500",
                    "relative px-3 py-2 cursor-pointer hover:bg-gray-100"
                  )}
                >
                  <time
                    dateTime={day.date}
                    className={
                      day.isToday
                        ? "flex size-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white"
                        : undefined
                    }
                  >
                    {day.date.split("-").pop().replace(/^0/, "")}
                  </time>
                  {day.employees.length > 0 && (
                    <ol className="mt-2">
                      {day.employees.map((employee) => (
                        <li key={employee.id} className="group flex">
                          <span className="flex w-full">
                            <time
                              dateTime={employee.date}
                              className="flex-auto truncate font-medium text-gray-900"
                            >
                              {convertDateToHourAndMinute(employee.shiftStart)}{" "}
                              - {convertDateToHourAndMinute(employee.shiftEnd)}
                            </time>
                            <p className="ml-3 hidden flex-none text-gray-500 xl:block ">
                              {employee.break} mins break 
                            </p>
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              ))}
            </div>
            <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
              {days.map((day) => (
                <button
                  key={day.date}
                  type="button"
                  onClick={() => setSelectedDay(day)}
                  className={classNames(
                    day.isCurrentMonth ? 'bg-white' : 'bg-gray-50',
                    (day.date === selectedDay?.date || day.isToday) && 'font-semibold',
                    day.date === selectedDay?.date && 'text-white',
                    day.date !== selectedDay?.date && day.isToday && 'text-indigo-600',
                    day.date !== selectedDay?.date && day.isCurrentMonth && !day.isToday && 'text-gray-900',
                    day.date !== selectedDay?.date && !day.isCurrentMonth && !day.isToday && 'text-gray-500',
                    'flex h-14 flex-col px-3 py-2 hover:bg-gray-100 focus:z-10'
                  )}
                >
                  <time
                    dateTime={day.date}
                    className={classNames(
                      day.date === selectedDay?.date && 'flex size-6 items-center justify-center rounded-full',
                      day.date === selectedDay?.date && day.isToday && 'bg-indigo-600',
                      day.date === selectedDay?.date && !day.isToday && 'bg-gray-900',
                      'ml-auto'
                    )}
                  >
                    {day.date.split("-").pop().replace(/^0/, "")}
                  </time>
                  <span className="sr-only">
                    {day.employees.length} employees
                  </span>
                  {day.employees.length > 0 && (
                    <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                      {day.employees.map((employee) => (
                        <span
                          key={employee.id}
                          className="mx-0.5 mb-1 size-1.5 rounded-full bg-gray-400"
                        />
                      ))}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {selectedDay?.employees.length > 0 && (
          <div className="px-4 py-10 sm:px-6 lg:hidden">
            <ol className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow ring-1 ring-black/5">
              {selectedDay.employees.map((employee) => (
                <li
                  key={employee.id}
                  className="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50 cursor-move"
                >
                  <div className="flex-auto">
                    {/* <p className="font-semibold text-gray-900">
                      {employee.name}
                    </p> */}
                    <time
                      dateTime={employee.date}
                      className="mt-2 flex items-center font-semibold text-gray-900"
                    >
                      <ClockIcon
                        className="mr-2 size-5 text-gray-400"
                        aria-hidden="true"
                      />
                      {convertDateToHourAndMinute(employee.shiftStart)} -{" "}
                      {convertDateToHourAndMinute(employee.shiftEnd)}
                    </time>
                    <p className="mt-2 text-gray-500">
                      Break: {employee.break} minutes
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </>
  );
};

export default MonthView;
